import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import type { KibSuperSelectOption } from '@chewy/kib-fields-new-react'
import { KibField, KibSuperSelect } from '@chewy/kib-fields-new-react'
import { KibCopyIcon, KibDeleteFillIcon } from '@chewy/kib-icons-react'
import { Utils } from '@pbt/pbt-ui-components'

import { RhapKibDatePicker } from '~/components/common/inputs/kib-date-picker/RhapKibDatePicker'
import { getInventoryOrdersIsUpdating } from '~/store/duck/inventoryOrders'
import { getInventoryDamageReasons } from '~/store/reducers/constants'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import { getOneUomPluralized } from '../../inventoryUtils'
import {
  parseReceiptExpirationDate,
  receiptExpirationDateToIsoDateTime,
} from '../utils'
import type { ReceiptFields } from './hooks/useReceiptFields'
import { OrderItemReceiptNotes } from './OrderItemReceiptNotes'
import { QuantityAutocompleteField } from './QuantityAutocompleteField'
import type { ReceiptFieldKey } from './receivingRequiredFields'

import '@chewy/kib-calendar-styles/dist/kib-calendar.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-super-select.css'
import styles from './OrderItemReceipt.module.scss'

interface OrderItemReceiptProps {
  canDelete?: boolean
  canDuplicate?: boolean
  fields: ReceiptFields
  isDraft?: boolean
  isViewOnly?: boolean
  onDelete?: () => void
  onDuplicate?: () => void
  orderedQuantity: number
  perPackageAmount: number
  productSizeUnit: string
  requiredFieldKeys: ReceiptFieldKey[]
}

export const OrderItemReceipt = ({
  canDelete = false,
  canDuplicate = false,
  fields,
  isDraft = false,
  isViewOnly = false,
  onDelete,
  onDuplicate,
  orderedQuantity,
  perPackageAmount,
  productSizeUnit,
  requiredFieldKeys,
}: OrderItemReceiptProps) => {
  const { t } = useTranslation('Admin')
  const isUpdating = useSelector(getInventoryOrdersIsUpdating)
  const InventoryDamageReasons = useSelector(getInventoryDamageReasons)
  const timezone = useCurrentBusinessTimezone()

  const {
    receivedQuantity,
    lotNumber,
    expirationDate,
    serialNumber,
    businessReferenceNumber,
    receiveReasonId,
    notes,
    rejected,
  } = fields

  const labelFor = (key: ReceiptFieldKey, label: string) =>
    requiredFieldKeys.includes(key) ? `${label} *` : label

  const isDamaged = Boolean(receiveReasonId.value)
  const RECEIVE_ACTION_VALUE = 'receive'
  const RECEIVE_DAMAGED_ACTION_VALUE = 'receive-damaged'

  const actionOptions: KibSuperSelectOption[] = [
    {
      label: t('Admin:CATALOG.ORDERS.RECEIVE_ACTION'),
      value: RECEIVE_ACTION_VALUE,
    },
    {
      label: t('Admin:CATALOG.ORDERS.RECEIVE_DAMAGED_ACTION'),
      value: RECEIVE_DAMAGED_ACTION_VALUE,
    },
    ...InventoryDamageReasons.map((reason) => ({
      label: t('Admin:CATALOG.ORDERS.REJECT_REASON_ACTION', {
        reason: reason.name,
      }),
      value: reason.id,
    })),
  ]

  const getSelectedActionValue = () => {
    if (!isDamaged) {
      return RECEIVE_ACTION_VALUE
    }
    if (!rejected.value) {
      return RECEIVE_DAMAGED_ACTION_VALUE
    }
    return receiveReasonId.value
  }

  const selectedActionValue = getSelectedActionValue()

  const selectedAction =
    actionOptions.find((opt) => opt.value === selectedActionValue) ??
    actionOptions[0]

  const handleActionChange = (selected: KibSuperSelectOption[]) => {
    const selectedValue = selected[0]?.value
    if (!selectedValue || selectedValue === RECEIVE_ACTION_VALUE) {
      rejected.setValue(false)
      receiveReasonId.setValue('')
    } else if (selectedValue === RECEIVE_DAMAGED_ACTION_VALUE) {
      rejected.setValue(false)
      const damagedReasonId = Utils.findConstantIdByName(
        'Damaged',
        InventoryDamageReasons,
      )
      receiveReasonId.setValue(damagedReasonId || '')
    } else {
      rejected.setValue(true)
      receiveReasonId.setValue(selectedValue)
    }
  }

  const inputDisabled = !isDraft || isUpdating || isViewOnly

  const receiptQuantity = receivedQuantity.value ?? 1
  const productSizeUnitLabel = getOneUomPluralized(
    productSizeUnit,
    receiptQuantity,
  )

  const expirationDatePickerValue = (() => {
    const dateOnly = parseReceiptExpirationDate(expirationDate.value, timezone)
    return dateOnly
      ? receiptExpirationDateToIsoDateTime(dateOnly, timezone)
      : null
  })()

  return (
    <div className={styles.container}>
      <div className={styles.fieldsRow}>
        <div className={styles.quantityWrapper}>
          <QuantityAutocompleteField
            autoFocus
            disabled={inputDisabled}
            field={receivedQuantity}
            invalid={receivedQuantity.errors.length > 0}
            label={labelFor('receivedQuantity', receivedQuantity.label!)}
            messagesContent={receivedQuantity.message}
            orderedQuantity={orderedQuantity}
            perPackageAmount={perPackageAmount}
          />
          <span className={styles.quantityLabel}>{productSizeUnitLabel}</span>
        </div>

        <KibSuperSelect
          disabled={inputDisabled}
          label={t('Admin:CATALOG.ORDERS.ACTION_LABEL')}
          options={actionOptions}
          value={[selectedAction]}
          onChange={handleActionChange}
        />

        <KibField
          disabled={inputDisabled}
          invalid={lotNumber.errors.length > 0}
          label={labelFor('lotNumber', lotNumber.label!)}
          messagesContent={lotNumber.message}
          value={lotNumber.value ?? ''}
          onChange={(e) => lotNumber.setValue(e.target.value)}
        />

        <KibField
          disabled={inputDisabled}
          invalid={serialNumber.errors.length > 0}
          label={labelFor('serialNumber', serialNumber.label!)}
          messagesContent={serialNumber.message}
          value={serialNumber.value ?? ''}
          onChange={(e) => serialNumber.setValue(e.target.value)}
        />

        <div className={styles.expirationDateWrapper}>
          <RhapKibDatePicker
            disabled={inputDisabled}
            invalid={expirationDate.errors.length > 0}
            label={labelFor('expirationDate', expirationDate.label!)}
            messagesContent={expirationDate.message}
            timezone={timezone}
            value={expirationDatePickerValue}
            onChange={(dateOrRaw) => {
              expirationDate.setValue(dateOrRaw ?? null)
            }}
          />
        </div>

        <KibField
          disabled={inputDisabled}
          invalid={businessReferenceNumber.errors.length > 0}
          label={labelFor(
            'businessReferenceNumber',
            businessReferenceNumber.label!,
          )}
          messagesContent={businessReferenceNumber.message}
          value={businessReferenceNumber.value ?? ''}
          onChange={(e) => businessReferenceNumber.setValue(e.target.value)}
        />
        {!isViewOnly && (
          <div className={styles.actionButtonContainer}>
            <KibButtonNew
              iconOnly
              className={styles.actionButton}
              disabled={!canDuplicate}
              emphasis="tertiary"
              icon={<KibCopyIcon />}
              size="small"
              onClick={onDuplicate}
            >
              {t('Common:DUPLICATE')}
            </KibButtonNew>
            <KibButtonNew
              iconOnly
              className={styles.actionButton}
              disabled={inputDisabled || !canDelete}
              emphasis="tertiary"
              icon={<KibDeleteFillIcon />}
              size="small"
              onClick={onDelete}
            >
              {t('Common:DELETE_ROW')}
            </KibButtonNew>
          </div>
        )}
      </div>
      <div className={styles.notesWrapper}>
        <OrderItemReceiptNotes
          field={notes}
          isDisabled={inputDisabled}
          isDraft={isDraft}
          required={isDamaged}
        />
      </div>
    </div>
  )
}
